/**
 * clientRecord — THE SEAM. The `ClientRecordSnapshot` read contract that
 * the client record page (PR-C1) renders and that PR-C2's `getClientRecord`
 * aggregate read MUST conform to (via the fixture-conformance test the
 * committed fixture anchors — see `src/lib/__fixtures__/clientRecord.ts`).
 *
 * House rule: FE types are hand-mirrored from the backend, NOT imported from
 * `api/_shared` (there is no bridge). Every vocabulary union below is a
 * hand-mirror of the canonical enum in `api/_shared/schema.ts` (the note-
 * pipeline projections, migration 0120) — keep them byte-aligned. The field
 * *names* here are the camelCase projection the aggregate GET returns; the
 * comments name each field's backing column/source so PR-C2's query covers
 * them.
 *
 * Frozen 2026-07-05 (PR-C1). Amendments require a CTO gate — this is the
 * contract a whole page-family + the gated backend hang on.
 *
 * AMENDMENT 2026-07-06 (GC-authorized lockstep, FE-integration pass):
 * `functionalStatus` — the tenth projection (migration 0122) — added in
 * one change across assembler + zod wire schema + this type + fixture +
 * both conformance tests.
 *
 * AMENDMENT 2026-07-06 (record-structure build, lockstep):
 * `vitals.transportationConsent` — migration 0127; the founder's
 * action-gating triad (may a care manager drive this client). Same
 * one-change discipline across assembler + zod + this type + fixture +
 * both conformance tests.
 */

import { displayValue } from '@/lib/valueCase';
import { fmtTime, proseDate } from '@/lib/proseDate';
import type { InHomeSupportKind } from '@/lib/inHomeSupportKinds';

// ---------------------------------------------------------------------
// Provenance — a discriminated union, never a flat note-link.
//
// A row's source event may be a note, a direct edit (no note), or an
// intake seed (no note). A derived field's provenance (transportation
// consent) is the latest matching record_event and may be null.
// The backend joins notes LEFT (never inner) so direct-edited / intake rows
// never drop.
// ---------------------------------------------------------------------
export type Provenance =
  | { kind: 'note'; eventId: string; noteId: string; noteDate: string } // "from note {noteDate} ↗" → Notes tab
  | { kind: 'direct_edit'; eventId: string; editedAt: string } // "edited {editedAt}" (no link)
  | { kind: 'intake_seed'; eventId: string }; // "from intake"

/** Derived-projection provenance: like Provenance, but nullable (housing). */
export type DerivedProvenance = Provenance | null;

// ---------------------------------------------------------------------
// Per-row envelope. Every Care Record row shares this; the BODY is typed
// per projection (one generic card is a lie — the six-lens review found
// each projection carries a materially different body).
// ---------------------------------------------------------------------
export interface RowEnvelope {
  id: string;
  provenance: Provenance;
  /**
   * The nullable second-event FK every projection carries (the discrepancy
   * dimension: `flagged_event_id`). Carried as a nullable slot NOW so a
   * later "show discrepancy" feature is not a contract amendment.
   */
  flagged: Provenance | null;
  /** Soft-archive state (`is_active`). Archived rows render muted, not dropped. */
  isActive: boolean;
}

// ---------------------------------------------------------------------
// Vocabulary unions — hand-mirrored from api/_shared/schema.ts (0120).
// ---------------------------------------------------------------------

/** clients.living_situation — CareTree's curated set. */
export type LivingSituation =
  | 'home'
  | 'apartment'
  | 'group_home'
  | 'independent_living'
  | 'assisted_living'
  | 'memory_care'
  | 'nursing_home'
  | 'rehab_facility'
  | 'hospice'
  | 'other';

/** clients.transportation_consent (0127 — the [M3] housing pattern).
 *  NULL column = not recorded; never a vocabulary member. */
export type TransportationConsent = 'consented' | 'declined';

/** clients.pronouns (0128 — the 0127 shape). NULL = not recorded and
 *  NULL never gates anything. */
export type ClientPronouns = 'she/her' | 'he/him' | 'they/them';

/** client_medications.status */
export type MedicationStatus = 'active' | 'discontinued';

/** client_conditions.status */
export type ConditionStatus = 'active' | 'watch' | 'resolved';

/** client_appointments.status — drives which date field renders (state machine). */
export type AppointmentStatus = 'scheduled' | 'attended' | 'missed' | 'cancelled';

/** client_appointments.recurrence (0144, Soak #2 Wave B / G2) — the cadence
 *  of a recurring appointment. ONE row represents the series; 'none' = a
 *  one-off. NOT NULL DEFAULT 'none', so always present on a row. */
export type AppointmentRecurrence = 'none' | 'weekly' | 'biweekly' | 'monthly';

/** The neutral "Weekly recurring" tag label, or null for a one-off ('none').
 *  ONE source of truth for the cadence tag across the card, the Overview
 *  rail, the Face Sheet, and the hero. */
export function recurrenceTagLabel(recurrence: AppointmentRecurrence | undefined): string | null {
  switch (recurrence) {
    case 'weekly':
      return 'Weekly recurring';
    case 'biweekly':
      return 'Recurring every two weeks';
    case 'monthly':
      return 'Monthly recurring';
    default:
      return null;
  }
}

/** client_risk_flags.level */
export type RiskLevel = 'watch' | 'elevated' | 'resolved';

/** client_legal_instruments.instrument */
export type LegalInstrument =
  | 'code_status'
  | 'polst'
  | 'healthcare_proxy'
  | 'dpoa'
  | 'will'
  | 'trust'
  | 'guardianship';

/** client_legal_instruments.status */
export type LegalStatus = 'on_file' | 'in_progress' | 'recommended' | 'missing' | 'unknown';

/** client_legal_instruments.code_status_values members (0136 — the SET;
 *  present iff instrument ∈ {code_status, polst}). dnr/dni/dnh combine;
 *  full/comfort/unknown are exclusive singletons. */
export type CodeStatus = 'full' | 'dnr' | 'dni' | 'dnh' | 'comfort' | 'unknown';

/**
 * Code-status display labels (DL v1 polish): plain words, not shouted
 * vocab values — "Full code" / "Comfort care", with the true acronyms
 * (DNR / DNI / DNH) kept as ALL-CAPS acronyms (the WS-3/WS-8 caps
 * ruling). 'unknown' reads honestly as "Not recorded" (never UNKNOWN),
 * matching the #437 ruling.
 */
export const CODE_STATUS_LABELS: Record<CodeStatus, string> = {
  full: 'Full code',
  dnr: 'DNR',
  dni: 'DNI',
  dnh: 'DNH',
  comfort: 'Comfort care',
  unknown: 'Not recorded',
};

export function codeStatusLabel(value: CodeStatus): string {
  return CODE_STATUS_LABELS[value];
}

/** The SET rendered as one label ("DNR + DNI + DNH"). A singleton set
 *  reads through its own label ('unknown' → "Not recorded"). */
export function codeStatusSetLabel(values: readonly CodeStatus[]): string {
  return values.map(codeStatusLabel).join(' + ');
}

/** client_hospital_events.kind */
export type HospitalEventKind =
  | 'er_visit'
  | 'admission'
  | 'discharge'
  | 'rehab_admission'
  | 'rehab_discharge';

/** client_coverage.program */
export type CoverageProgram =
  | 'medicare_a'
  | 'medicare_b'
  | 'medicare_a_b'
  | 'medicare_advantage'
  | 'part_d'
  | 'supplement'
  | 'medicaid'
  | 'ltc_insurance'
  | 'private'
  | 'other';

/** client_coverage.status */
export type CoverageStatus = 'active' | 'inactive' | 'pending' | 'unverified';

/** client_functional_status.activity — the 0122 assessment grid (9 ADLs
 *  + 9 IADLs). Value-mirror of FUNCTIONAL_ACTIVITIES in
 *  src/lib/noteVocabulary.ts (cards iterate THAT array, never this union). */
export type FunctionalActivity =
  | 'bathing'
  | 'grooming'
  | 'toileting'
  | 'dressing_upper'
  | 'dressing_lower'
  | 'transfer_chair'
  | 'transfer_bed'
  | 'ambulation'
  | 'feeding'
  | 'meal_prep'
  | 'housekeeping'
  | 'laundry'
  | 'medication_setup'
  | 'medication_reminders'
  | 'finances'
  | 'shopping'
  | 'telephone'
  | 'driving';

/** client_functional_status.level — 'not_assessed' is the honest
 *  in-vocabulary value (the known-unknowns signal). */
export type FunctionalLevel = 'independent' | 'needs_assistance' | 'dependent' | 'not_assessed';

/**
 * The provider link — a 3-arm tagged union (the client_providers three-arm
 * FK: directory_individual_id / directory_org_id / pending_resource_id, at
 * most one set per the named num_nonnulls CHECK; ZERO arms = unlinked,
 * renders by displayName).
 */
export type ProviderLink =
  | { kind: 'individual'; id: string } // -> Directory individual ↗
  | { kind: 'org'; id: string } // -> Directory org ↗
  | { kind: 'pending'; resourceId: string } // -> suggested_resources (steward pend)
  | { kind: 'unlinked' }; // renders by display_name only

// ---------------------------------------------------------------------
// The eleven projections + housing (typed bodies). Each row = RowEnvelope
// ∧ a projection-specific body.
// ---------------------------------------------------------------------

/** When a medication is taken: the medication list's five blocks. */
export type MedicationTimeOfDay = 'morning' | 'afternoon' | 'evening' | 'bedtime' | 'as_needed';

export type MedicationRow = RowEnvelope & {
  name: string; // PHI
  dose?: string; // TEXT by design (the anti-EHR rule; no sig-coding)
  /** The dose it was created with, when its creation is linked (0232). */
  firstDose?: string;
  /** The directions: anything about taking it besides the time of day. */
  schedule?: string;
  prescriber?: string; // resolved display of prescriber_provider_id
  /** The care-team row behind `prescriber` (client_providers.id) — present
   *  iff prescriber_provider_id resolved (directory care-web, 2026-07-10);
   *  a payload-only prescriber string carries no id (never guess identity). */
  prescriberId?: string;
  reason?: string;
  status: MedicationStatus;
  startedOn?: string;
  endedOn?: string;
  /** When it is taken, in the medication list's order; absent when none is recorded. */
  timesOfDay?: MedicationTimeOfDay[];
};

export type ConditionRow = RowEnvelope & {
  name: string; // PHI
  status: ConditionStatus;
  notedOn?: string;
  note?: string;
};

/** "Name (status)": the status rides in parentheses only when it is not
 *  the plain active default, so the condition worth a second look is the
 *  one that says so. Conditions carry a status, never a severity. */
export function formatConditionEntry(c: Pick<ConditionRow, 'name' | 'status'>): string {
  return c.status !== 'active' ? `${c.name} (${c.status})` : c.name;
}

export type AppointmentRow = RowEnvelope & {
  provider: string; // resolved display of provider_id
  /** The care-team row behind `provider` (client_providers.id) — present
   *  iff provider_id resolved (directory care-web, 2026-07-10); a
   *  payload-only provider string carries no id (never guess identity). */
  providerId?: string;
  status: AppointmentStatus;
  scheduledFor?: string; // shown when status='scheduled'
  scheduledTime?: string; // 0218: 'HH:MM' in the practice's day; shown beside the scheduled date
  occurredOn?: string; // shown when status ∈ {attended, missed}
  dateText?: string; // fallback display when neither structured date is set
  purpose?: string; // payload-only — from the appointment-proposal payload (record_events.payload), not a projected column
  recurrence: AppointmentRecurrence; // 0144; NOT NULL DEFAULT 'none' — always present
  location?: string; // 0144; where the appointment happens (plain PII); omit-never-null
  // 0233: the place behind `location`: its name, its parts (only with a
  // street), and the Directory entry it was copied from.
  locationName?: string;
  locationParts?: { line1: string; line2: string | null; city: string | null; state: string | null; zip: string | null };
  locationDirectoryOrgId?: string;
  locationDirectoryIndividualId?: string;
  outcome?: string; // PHI
};

/**
 * B1b (design review 2026-07-06): a 'scheduled' appointment whose date
 * has passed with no attended/missed note must not keep rendering
 * "Scheduled". No timers exist under Era A, so the state DERIVES at
 * read/render time against the viewer's local day (the cmHome
 * visit-today precedent: the CM's day decides day-boundary questions,
 * never the server's timezone). ISO dates compare lexicographically.
 * A dateText-only row (no structured date) can't be judged — stays
 * Scheduled.
 */
export function appointmentDatePassed(
  row: Pick<AppointmentRow, 'status' | 'scheduledFor'>,
  todayIso: string,
): boolean {
  return (
    row.status === 'scheduled' &&
    typeof row.scheduledFor === 'string' &&
    row.scheduledFor < todayIso
  );
}

/** A row whose visit's date has passed and that carries no note yet: the
 *  row offers "Add a note". A visit that happened or was missed has
 *  passed; a scheduled or cancelled one once its date is before today. A
 *  recurring series repeats, so it never has. */
export function appointmentAwaitsNote(
  row: Pick<AppointmentRow, 'status' | 'scheduledFor' | 'recurrence' | 'outcome'>,
  todayIso: string,
): boolean {
  if (isRecurring(row) || (row.outcome ?? '').trim() !== '') return false;
  if (row.status === 'attended' || row.status === 'missed') return true;
  return typeof row.scheduledFor === 'string' && row.scheduledFor < todayIso;
}

/** A live recurring series (cadence set to something other than 'none').
 *  ONE row represents the whole series (0144); defensive against an
 *  undefined recurrence on a hand-built row. */
export function isRecurring(row: Pick<AppointmentRow, 'recurrence'>): boolean {
  return row.recurrence !== undefined && row.recurrence !== 'none';
}

// Epoch-day helpers (UTC calendar days; timezone-stable — the parts are a
// calendar day, not an instant, so the arithmetic never drifts by the
// viewer's zone). Mirrors upcomingAppointments.oneMonthOut's discipline.
function epochDay(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Math.floor(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) / 86_400_000);
}
function isoFromEpochDay(n: number): string {
  return new Date(n * 86_400_000).toISOString().slice(0, 10);
}

/**
 * The DISPLAY-ONLY next occurrence of an appointment on or after `today`
 * (0144, Soak #2 Wave B / G2). A recurring series is still ONE row — this
 * NEVER persists or fabricates instances; it advances the stored anchor
 * forward by the cadence (weekly +7d, biweekly +14d, monthly +1 calendar
 * month, clamped month-end-safe so Jan 31 -> Feb 28 then -> Mar 31) until
 * it is >= today, purely so the sort and the date display use the NEXT
 * occurrence rather than a stale past anchor. Non-recurring rows, or rows
 * with no structured scheduledFor anchor, return scheduledFor unchanged
 * (undefined stays undefined). All dates are 'YYYY-MM-DD'.
 */
/**
 * A scheduled appointment's next date in words, with its time when one is
 * set: 'August 27, 2026 at 2:30 PM'. A recurring series keeps its time on
 * every occurrence. Undefined when the row has no structured date.
 */
export function appointmentWhen(
  row: Pick<AppointmentRow, 'recurrence' | 'scheduledFor' | 'scheduledTime'>,
  todayIso: string,
): string | undefined {
  const date = nextOccurrence(row, todayIso);
  if (date === undefined) return undefined;
  return row.scheduledTime ? `${proseDate(date)} at ${fmtTime(row.scheduledTime)}` : proseDate(date);
}

export function nextOccurrence(
  row: Pick<AppointmentRow, 'recurrence' | 'scheduledFor'>,
  todayIso: string,
): string | undefined {
  const anchor = row.scheduledFor;
  if (typeof anchor !== 'string') return anchor;
  if (!isRecurring(row)) return anchor;
  if (anchor >= todayIso) return anchor; // already upcoming — no advance
  if (row.recurrence === 'weekly' || row.recurrence === 'biweekly') {
    const period = row.recurrence === 'weekly' ? 7 : 14;
    const start = epochDay(anchor);
    const periods = Math.ceil((epochDay(todayIso) - start) / period);
    return isoFromEpochDay(start + periods * period);
  }
  // monthly: advance ONE calendar month at a time from the ORIGINAL anchor
  // day, clamping to the target month's last day (Jan 31 -> Feb 28), so the
  // day-of-month intent is preserved across short months.
  const [y, m, d] = anchor.split('-').map(Number);
  const day = d ?? 1;
  let cy = y ?? 1970;
  let cm = m ?? 1; // 1-based
  let candidate = anchor;
  // Defensively bounded (a decade of months) — never an infinite loop.
  for (let i = 0; i < 600 && candidate < todayIso; i++) {
    cm += 1;
    if (cm > 12) {
      cm = 1;
      cy += 1;
    }
    const lastDay = new Date(Date.UTC(cy, cm, 0)).getUTCDate();
    const cd = Math.min(day, lastDay);
    candidate = `${String(cy).padStart(4, '0')}-${String(cm).padStart(2, '0')}-${String(cd).padStart(2, '0')}`;
  }
  return candidate;
}

export type RiskFlagRow = RowEnvelope & {
  category: string; // tenant-config vocabulary (NO DB CHECK by design)
  level: RiskLevel;
  basis?: string; // PHI
};

export type ProviderRow = RowEnvelope & {
  displayName: string; // renders while unlinked/pending (PHI-adjacent)
  role?: string;
  specialty?: string; // comes FROM the directory entry, never the note
  organization?: string;
  officePhone?: string; // WS-5 (0140): the row's own per-client value
  officeAddress?: string;
  email?: string; // 0231: the row's own email; the Directory's shows when unset
  directoryPhone?: string; // the linked Directory entry's value (display fallback)
  directoryAddress?: string;
  directoryEmail?: string; // the linked Directory entry's email
  // 0234: the row's own office address as its five parts (only with a
  // street; a text-only row has officeAddress alone), and the linked
  // entry's parts, which the forms tag "From the Directory".
  officeAddressParts?: { line1: string; line2: string | null; city: string | null; state: string | null; zip: string | null };
  directoryAddressParts?: { line1: string; line2: string | null; city: string | null; state: string | null; zip: string | null };
  directoryName?: string; // 0217: the linked Directory entry's current name
  recordedName?: string; // the row's stored name, only when it differs from displayName
  nameIsOverride?: true; // a person typed this client's own name for the linked row
  link: ProviderLink; // the 3-arm tagged union
};

export type ContactRow = RowEnvelope & {
  name: string; // PHI
  relationship?: string;
  phone?: string;
  email?: string;
  /** The contact's mailing/street address (0137 — founder addition). PHI. */
  address?: string;
  /** 0226: the address's five parts; absent on every part: text only. */
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressState?: string;
  addressZip?: string;
  isPoa: boolean; // render as toggles
  isHcp: boolean;
  /** Legal guardian (0150) — a first-class relationship flag, like POA/HCP;
   *  distinct from the Legal & wishes guardianship instrument. */
  isGuardian: boolean;
  isEmergency: boolean;
  livesWith: boolean;
  /** Primary Billing Contact (0137, WS-4 D4) — the human-relationship
   *  fact; deliberately separate from the invoice bill-to (0095). */
  isBilling: boolean;
  /** 0227: the Directory entry the contact is (at most one), and that
   *  entry's name, phone, email and address as the Directory has them now
   *  (an organization's merge chain followed). The contact's own phone,
   *  email and address above are only what was typed for this client;
   *  contactEffective says which a screen shows. */
  directoryIndividualId?: string;
  directoryOrgId?: string;
  directoryName?: string;
  directoryPhone?: string;
  directoryEmail?: string;
  directoryAddress?: string;
  directoryAddressLine1?: string;
  directoryAddressLine2?: string;
  directoryAddressCity?: string;
  directoryAddressState?: string;
  directoryAddressZip?: string;
};

/** A contact's phone, email and address as a screen shows them. */
export interface ContactEffective {
  phone: string | null;
  email: string | null;
  address: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  addressCity: string | null;
  addressState: string | null;
  addressZip: string | null;
}

/**
 * A contact's phone, email and address as shown (0227): each of phone and
 * email is the contact's own when set, else its Directory entry's; the
 * address is the contact's own text and parts when its own text is set,
 * else the entry's text and parts, never a mix of the two. An unlinked
 * contact is its own values. The server's twin is contactEffective in
 * api/_shared/contactEffective.ts (the bill-to projects it).
 */
export function contactEffective(row: ContactRow): ContactEffective {
  const set = (v: string | undefined): v is string => v !== undefined && v.trim() !== '';
  const linked = row.directoryIndividualId !== undefined || row.directoryOrgId !== undefined;
  const own: ContactEffective = {
    phone: row.phone ?? null,
    email: row.email ?? null,
    address: row.address ?? null,
    addressLine1: row.addressLine1 ?? null,
    addressLine2: row.addressLine2 ?? null,
    addressCity: row.addressCity ?? null,
    addressState: row.addressState ?? null,
    addressZip: row.addressZip ?? null,
  };
  if (!linked) return own;
  const ownAddress = set(row.address);
  return {
    phone: set(row.phone) ? row.phone : (row.directoryPhone ?? null),
    email: set(row.email) ? row.email : (row.directoryEmail ?? null),
    address: ownAddress ? own.address : (row.directoryAddress ?? null),
    addressLine1: ownAddress ? own.addressLine1 : (row.directoryAddressLine1 ?? null),
    addressLine2: ownAddress ? own.addressLine2 : (row.directoryAddressLine2 ?? null),
    addressCity: ownAddress ? own.addressCity : (row.directoryAddressCity ?? null),
    addressState: ownAddress ? own.addressState : (row.directoryAddressState ?? null),
    addressZip: ownAddress ? own.addressZip : (row.directoryAddressZip ?? null),
  };
}

export type HospitalEventRow = RowEnvelope & {
  kind: HospitalEventKind;
  facility?: string; // PHI
  facilityOrgId?: string; // the Directory organization, when the stay was linked
  eventOn?: string;
  dateText?: string;
  note?: string; // PHI
};

export type LegalInstrumentRow = RowEnvelope & {
  instrument: LegalInstrument;
  status: LegalStatus;
  /** The code-status SET (0136): present iff instrument ∈ {code_status,
   *  polst} (re-anchored alignment CHECK [m4]). Supersedes the single
   *  codeStatusValue. */
  codeStatusValues?: CodeStatus[];
  designee?: string; // PHI
  effectiveOn?: string;
};

/** client_allergies.severity (0136) — app-validated vocabulary; absent =
 *  not stated. */
export type AllergySeverity = 'mild' | 'moderate' | 'severe' | 'unknown';

/**
 * The ELEVENTH projection (migration 0136 — WS-3 clinical safety
 * fields): the allergy list, target-row class — same envelope as
 * medications. An empty array means NOT RECORDED, never "no allergies"
 * (the explicit NKDA confirmed state is a flagged follow-on ruling).
 */
export type AllergyRow = RowEnvelope & {
  substance: string; // PHI
  reaction?: string; // PHI
  severity?: AllergySeverity;
};

export type CoverageRow = RowEnvelope & {
  program: CoverageProgram;
  provider?: string; // PHI-adjacent — the carrier / org name (0146)
  status: CoverageStatus; // client_coverage.status is NOT NULL (schema.ts, 0120:988) — always present
  verifiedOn?: string;
  detail?: string; // PHI-adjacent
  // The card's fields (0213), PHI; each omitted when not recorded.
  planName?: string;
  memberId?: string;
  groupNumber?: string;
  cardPhone?: string;
  rxBin?: string;
  rxPcn?: string;
  rxGroup?: string;
  holderName?: string;
  holderRelationship?: string;
};

/**
 * The TENTH projection (migration 0122; lockstep contract amendment,
 * GC-authorized 2026-07-06 with the FE-integration pass): functional
 * status (ADL/IADL), register class — one row per (tenant, client,
 * activity). Same envelope as riskFlags. Activities the record has not
 * yet captured have NO row (the card renders those as implicit
 * "not yet captured" ghosts, distinct from an explicit not_assessed row).
 */
export type FunctionalStatusRow = RowEnvelope & {
  activity: FunctionalActivity;
  level: FunctionalLevel;
  note?: string; // PHI
};

/** clients.relationship_status (0221). Mirrors the server type. */
export type RelationshipStatus = 'single' | 'married' | 'widowed' | 'divorced';

/** The client's own Referred by (0221) when it holds one, else the latest
 *  intake card's. Mirrors api/_shared/clientRecordSnapshot.ts ReferredBy. */
export interface ReferredBy {
  label: string;
  source: 'client' | 'intake';
  orgId: string | null;
  individualId: string | null;
}

/** LP-33 (0247): a place on the client, the preferred hospital or the
 *  pharmacy. A Directory pick carries its id and the Directory's phone
 *  and address; a typed name carries the name alone. Mirrored in
 *  api/_shared/clientRecordSnapshot.ts. */
export interface ClientPlace {
  orgId: string | null;
  name: string;
  phone: string | null;
  address: string | null;
}

/** clients.gender (0137, WS-4 D1). App-validated (CLIENT_GENDER_VALUES),
 *  NO DB CHECK; NULL = not recorded, never a member. */
export type ClientGender = 'female' | 'male' | 'nonbinary' | 'prefer_not_to_say' | 'unknown';

/** clients.engagement (0137, WS-4 D3). App vocabulary, no DB CHECK;
 *  NOT NULL DEFAULT care_management. */
export type ClientEngagement = 'care_management' | 'spice' | 'both';

/** clients.lifecycle_status (0145, Soak #2 Wave B / G6, D2). The client's
 *  real-world lifecycle marker — ORTHOGONAL to isActive (a deceased client
 *  can be isActive=true). DB CHECK-backed; NOT NULL DEFAULT active.
 *  'archived' is NOT a member: "Archived" derives from !isActive. Mirrored
 *  in api/_shared/clientRecordSnapshot.ts. */
export type ClientLifecycleStatus =
  | 'active'
  | 'inactive_awaiting_payment'
  | 'deceased'
  | 'deceased_family_work';

/** The most a status note holds (the server's MAX_STATUS_NOTE). */
export const STATUS_NOTE_MAX = 100;

/**
 * How a stored status note reads: through the one display rule for a
 * one-line value (valueCase.ts displayValue, the 'value' scope: a first
 * capital, no dictionary), on the trimmed note; empty for null or a blank
 * note. The one display seam for the note in the browser;
 * api/_shared/clientRecordSnapshot.ts holds the server's twin.
 */
export function displayStatusNote(note: string | null | undefined): string {
  return note == null ? '' : displayValue(note.trim());
}

/** The status label, then the shown note in brackets when there is one. */
function withStatusNote(label: string, statusNote: string | null | undefined): string {
  const shown = displayStatusNote(statusNote);
  return shown === '' ? label : `${label} (${shown})`;
}

/** The header lifecycle badge — the ONE source of truth mapping the
 *  marker, the archive flag and the note to a register-safe { label,
 *  variant }. The label is LIFECYCLE_STATUS_LABELS (the map the dialog
 *  shares), then the shown note in brackets: "Inactive (Awaiting
 *  payment)". Register (brand): affirm=green (Active), caution=gold (a
 *  pending/attention state), neutral=sand (a quiet fact); the variant
 *  follows the status alone, never the note. Death is NOT an alarm —
 *  deceased is a QUIET neutral marker, never risk/pepper. ARCHIVE
 *  OVERRIDES: when !isActive the badge is a neutral "Archived" whatever
 *  the lifecycle and the note (the orthogonal-axes rule). Variant is a
 *  narrow Chip-variant subset. */
export function lifecycleBadge(
  lifecycleStatus: ClientLifecycleStatus,
  isActive: boolean,
  statusNote?: string | null,
): { label: string; variant: 'affirm' | 'caution' | 'neutral' } {
  if (!isActive) return { label: 'Archived', variant: 'neutral' };
  switch (lifecycleStatus) {
    case 'active':
      return { label: withStatusNote(LIFECYCLE_STATUS_LABELS.active, statusNote), variant: 'affirm' };
    case 'inactive_awaiting_payment':
      return {
        label: withStatusNote(LIFECYCLE_STATUS_LABELS.inactive_awaiting_payment, statusNote),
        variant: 'caution',
      };
    case 'deceased':
      return { label: withStatusNote(LIFECYCLE_STATUS_LABELS.deceased, statusNote), variant: 'neutral' };
    case 'deceased_family_work':
      return {
        label: withStatusNote(LIFECYCLE_STATUS_LABELS.deceased_family_work, statusNote),
        variant: 'neutral',
      };
    // A lifecycle value this bundle doesn't know (a newer server
    // vocabulary reaching an older client, a retired one such as 'opg', or
    // a future member added
    // without this switch). The caller destructures .label/.variant, so
    // a closed switch returning undefined would CRASH the client-record
    // header. Degrade to a quiet neutral chip with the spaced raw form
    // (the recurrenceTagLabel/accessRoleLabel degrade discipline), never
    // an alarm register. The note rides along the same way.
    default: {
      const spaced = String(lifecycleStatus).replace(/_/g, ' ').trim();
      const label = spaced
        ? spaced.charAt(0).toUpperCase() + spaced.slice(1)
        : 'Unknown status';
      return { label: withStatusNote(label, statusNote), variant: 'neutral' };
    }
  }
}

/** Where a client sits in a list ordered by life: an active client first,
 *  every other lifecycle state after (inactive awaiting payment, deceased,
 *  deceased with family work continuing). A value this bundle does not
 *  know sorts with the active ones rather than hiding a client at the
 *  foot. */
export function lifecycleListRank(lifecycleStatus: ClientLifecycleStatus): 0 | 1 {
  switch (lifecycleStatus) {
    case 'inactive_awaiting_payment':
    case 'deceased':
    case 'deceased_family_work':
      return 1;
    default:
      return 0;
  }
}

/** The one label per status, shared by the header chip and the Change
 *  client status dialog. */
export const LIFECYCLE_STATUS_LABELS: Record<ClientLifecycleStatus, string> = {
  active: 'Active',
  inactive_awaiting_payment: 'Inactive',
  deceased: 'Deceased',
  deceased_family_work: 'Deceased, family work continues',
};

/** One line per marker, said in terms of the two surfaces it actually
 *  changes: the client map (clientLifecycle.ts's MAP_PLOTTED_LIFECYCLE
 *  on the server) and the caseload (CASELOAD_EXCLUDED_LIFECYCLE). The
 *  status picker (LifecycleStatusDialog) shows this beside each option
 *  so the choice reads as a real-world consequence, not a vocabulary
 *  word. Server behavior is the ONE source of truth; this is prose
 *  describing it, kept in the same file as the labels it sits beside. */
export const LIFECYCLE_STATUS_EFFECT: Record<ClientLifecycleStatus, string> = {
  active: 'On the map and the caseload.',
  inactive_awaiting_payment: 'Off the map, kept on the caseload.',
  deceased: 'Off the map and the caseload.',
  deceased_family_work: 'Off the map, kept on the caseload while family work runs.',
};

/** client_addresses.label (0137, WS-4 D2). App vocabulary, no DB CHECK. */
export type ClientAddressLabel = 'home' | 'facility' | 'mailing' | 'other';

/** client_addresses (0137, WS-4 D2) — the "currently at" model. NOT a
 *  RowEnvelope: operator-entered rows (change_log-trailed server-side),
 *  no note-pipeline provenance. Optional fields OMIT per the wire rule. */
export type AddressRow = {
  id: string;
  label: ClientAddressLabel;
  line1: string;
  line2?: string;
  city?: string;
  state?: string;
  zip?: string;
  /** Where the client is RIGHT NOW when it differs from home. */
  isCurrentLocation: boolean;
  /** The canonical/mailing identity address. */
  isPrimary: boolean;
  isActive: boolean;
  /** 0225: the type of place and since when. Absent = not recorded. */
  placeType?: PlaceType;
  placeSince?: string;
  /** 0241: the facility or place name. Absent = none. */
  placeName?: string;
};

/** In-home support (0235): the client's kinds in the card's order,
 *  each with at most one provider and, for other, its note. A Directory
 *  organization or person carries its name, phone and address from the
 *  Directory; a typed provider is its name. An empty list is "Not
 *  recorded". Mirrors the server's InHomeSupportWire. */
export type InHomeSupportProvider =
  | { kind: 'organization' | 'individual'; id: string; name: string; phone: string | null; address: string | null }
  | { kind: 'text'; name: string };
export interface InHomeSupportKindWire {
  kind: InHomeSupportKind;
  provider: InHomeSupportProvider | null;
  note: string | null;
}
export interface InHomeSupportWire {
  kinds: InHomeSupportKindWire[];
}

/** client_addresses.place_type (0225). Mirrors the server type. */
export type PlaceType =
  | 'home'
  | 'apartment'
  | 'with_family'
  | 'independent_living'
  | 'group_home'
  | 'assisted_living'
  | 'memory_care'
  | 'nursing_home'
  | 'rehab_facility'
  | 'hospital'
  | 'hospice'
  | 'mailing'
  | 'other';

// ---------------------------------------------------------------------
// The vitals band sub-object — fields are NOT all on `clients`.
// Each field names its source for PR-C2's query.
// ---------------------------------------------------------------------
export interface VitalsBand {
  displayName: string; // clients.display_name
  /** clients.first_name / last_name (structured name, migration 0100). The
   *  name-edit dialog prefills from these when firstName is present, else
   *  splits displayName. NULL = never captured (legacy import). Mirrored in
   *  api/_shared/clientRecordSnapshot.ts. */
  firstName: string | null;
  lastName: string | null;
  /**
   * Human lifecycle label (a fallback text form). Lifecycle-aware (0145):
   * !isActive → 'Archived client' (archive overrides); else derived from
   * `lifecycleStatus`. The badge uses `lifecycleStatus` + `isActive` (not
   * this string) for its register. Mirrored server-side.
   */
  status: string | null;
  /** clients.lifecycle_status raw (0145, Soak #2 Wave B / G6, D2): the
   *  real-world marker. ORTHOGONAL to isActive — a deceased client can be
   *  isActive=true. The header badge maps this + isActive to a
   *  label/variant (lifecycleBadge); when !isActive the badge shows
   *  "Archived" and IGNORES this. Always present (NOT NULL DEFAULT active).
   *  Mirrored in api/_shared/clientRecordSnapshot.ts. */
  lifecycleStatus: ClientLifecycleStatus;
  /** clients.status_note raw: the short note shown after the status in
   *  brackets, as typed; null when there is none. The display adds the
   *  capital (displayStatusNote). Mirrored in api/_shared/clientRecordSnapshot.ts. */
  statusNote: string | null;
  /** clients.is_active raw (TABLE-STAKES TS-5, additive 2026-07-06): drives
   *  the record page's Archive/Restore affordance without string-matching
   *  the derived `status` label. Mirrored in api/_shared/clientRecordSnapshot.ts. */
  isActive: boolean;
  /** clients.is_demo raw (board demo sandbox, 0156): true = a demo/test
   *  client. The note-authoring surfaces go live for a write_demo_clinical
   *  caller (board_observer) ONLY on an is_demo client; the server fences the
   *  writes regardless. Mirrored in api/_shared/clientRecordSnapshot.ts. */
  isDemo: boolean;
  /** Whether a client_photos row exists (0142, the humanity upgrade): the
   *  hero + Face Sheet upgrade the gold initials circle to the photo when
   *  true, lazily loading it from GET .../photo. Mirrored server-side. */
  hasPhoto: boolean;
  /** From the legal_instruments code_status/polst row — the whole
   *  recorded SET (0136: "DNR + DNI"). ['unknown'] = recorded-as-unknown;
   *  null = no code-status row at all. */
  codeStatus: { values: CodeStatus[]; provenance: Provenance } | null;
  /** clients.transportation_consent (0127 — the founder's action-gating
   *  triad; lockstep amendment, record-structure build 2026-07-06).
   *  NULL = not recorded (the honest third state). Provenance derives
   *  from the latest entity='transportation' event (housing pattern)
   *  and may itself be null. Mirrored in api/_shared/clientRecordSnapshot.ts. */
  transportationConsent: { value: TransportationConsent; provenance: DerivedProvenance } | null;
  /** clients.pronouns (0128). NULL = not recorded — the band renders
   *  "Not recorded" and nothing gates on it. No provenance object: an
   *  operator-set demographic (change_log-trailed server-side), not a
   *  note-derived fact. Mirrored in api/_shared/clientRecordSnapshot.ts. */
  pronouns: ClientPronouns | null;
  /** Demographics (0137, WS-4 D1): plain PII columns on clients (the
   *  full_name posture). NULL = not recorded everywhere; operator-set
   *  (change_log-trailed server-side), no provenance objects. dob is
   *  'YYYY-MM-DD'. Mirrored in api/_shared/clientRecordSnapshot.ts. */
  dob: string | null;
  /** The CareTree fields (0221): plain values, null = not recorded.
   *  'YYYY-MM-DD' for the dates. Mirrored in api/_shared/clientRecordSnapshot.ts. */
  dateOfDeath: string | null;
  /** The day the client went on hospice (0243), 'YYYY-MM-DD'; null = not
   *  on hospice. Set and cleared from Change client status. */
  onHospiceSince: string | null;
  relationshipStatus: RelationshipStatus | null;
  serviceStartDate: string | null;
  /** LP-33 (0247): the language the client speaks first; null = not
   *  recorded. An office field. Mirrored in api/_shared/clientRecordSnapshot.ts. */
  primaryLanguage: string | null;
  /** LP-33 (0247): the preferred hospital and the pharmacy, a Directory
   *  pick with its phone and address or a typed name; null = not recorded.
   *  Mirrored in api/_shared/clientRecordSnapshot.ts. */
  preferredHospital: ClientPlace | null;
  pharmacy: ClientPlace | null;
  gender: ClientGender | null;
  phone: string | null;
  email: string | null;
  /** clients.preferred_name (0147, Soak #2 Wave B): the name the client
   *  goes by ("Chuck"). Plain PII (the demographics posture); NULL = not
   *  recorded — the header shows nothing extra when blank. Operator-set
   *  (change_log-trailed server-side), no provenance object. Mirrored in
   *  api/_shared/clientRecordSnapshot.ts. */
  preferredName: string | null;
  /** clients.engagement (0137, WS-4 D3): what Peace does for this client.
   *  NOT NULL server-side (DEFAULT care_management). */
  engagement: ClientEngagement;
  /** ELEVATED-only filter over risk_flags (watch flags do NOT surface here). */
  riskPills: { category: string; level: 'elevated' }[];
  /** The lead care manager: clients.primary_cm_roster_id → staff_roster
   *  join. Null when unassigned. `phone` is the roster's WORK phone
   *  (staff_roster.phone — the intro-email direct line, not the
   *  private-contact personal_phone tier); null when none is on file. */
  owner: { rosterId: string; displayName: string; phone: string | null; email?: string } | null;
  /** True when the signed-in viewer is one of the client's care managers:
   *  the login behind the lead's roster row, or behind an additional care
   *  manager's. False when neither has a linked login matching the viewer.
   *  Drives the confirm queue's viewer-relative care-notice label
   *  (ConfirmQueue.tsx). */
  viewerIsCareManager: boolean;
  /** The lead SPICE therapist: clients.spice_therapist_roster_id →
   *  staff_roster join. Meaningful when engagement is spice/both; may
   *  linger through transitions. `phone` is the same roster work-phone
   *  field as `owner.phone`. */
  spiceTherapist: { rosterId: string; displayName: string; phone: string | null } | null;
  /** The additional care managers (client_staff_assignments): active rows
   *  only, the lead excluded, ordered by name. Same shape as `owner`.
   *  Mirrored in api/_shared/clientRecordSnapshot.ts. */
  additionalCareManagers: { rosterId: string; displayName: string; phone: string | null }[];
  /** The additional SPICE therapists, the same way. */
  additionalSpiceTherapists: { rosterId: string; displayName: string; phone: string | null }[];
  /** The SPICE schedule (migrations 0187, 0203), folded into this
   *  aggregate so the record page's schedule and its doors cost no second
   *  fetch. null = no schedule row yet, or the client is pure
   *  care_management (never queried). `status` is one word for the
   *  plan's state on the practice day (active, covered, or a pause's
   *  word); this client reads the state through effectiveHold. The
   *  reason, note and until travel while the plan is not regular, and
   *  the cover and her name only while it is covered. The optional
   *  plan-level fields ride beside the sessions for older pages and are
   *  never read here. Mirrored in api/_shared/clientRecordSnapshot.ts
   *  SpiceScheduleWire. */
  spiceSchedule: {
    status: 'active' | 'covered' | 'on_hold' | 'vacation' | 'hospital';
    holdReason: 'vacation' | 'medical' | 'other' | null;
    holdNote: string | null;
    statusUntil: string | null;
    coverRosterId: string | null;
    coverName: string | null;
    sessions: {
      weekday: number;
      startTime: string;
      kind: 'spice' | 'spice_friendly_visitor';
      frequency: 'weekly' | 'every_two_weeks' | 'monthly';
      anchorWeek: string | null;
      weekOfMonth: 'first' | 'second' | 'third' | 'fourth' | 'last' | null;
      durationMinutes: number | null;
      /** The session's own therapist and her name: present only when she
       *  is not the client's own SPICE therapist. */
      therapistRosterId?: string;
      therapistName?: string;
    }[];
    cadence?: 'weekly' | 'biweekly' | 'twice_weekly' | null;
    slots?: { weekday: number; startTime: string; durationMinutes: number | null }[];
    biweeklyAnchor?: string | null;
    statusReason?: string | null;
  } | null;
  /** The client's soonest care visit (a care manager's planned visit) on
   *  the practice day or later, by any care manager; null when none.
   *  Optional for an older cached shape. Mirrored in
   *  api/_shared/clientRecordSnapshot.ts. */
  nextCareVisit?: { date: string; time: string | null; careManagerName: string } | null;
  /** Whether THIS caller may change the client's SPICE schedule, its
   *  status or its cover: canEditClientSchedule (manage_client_identity),
   *  computed server-side. The record's schedule doors read
   *  canEditClientSchedule (src/lib/capabilities.ts), which answers the
   *  same. */
  canEditSpiceSchedule: boolean;
  /** Derived from coverage rows (e.g. "Medicare A + B + Medicaid"). */
  coverageSummary: string | null;
  /** From the intake work_item. NULL for non-intake clients. */
  referredBy: ReferredBy | null;
  /** Derived MAX(record_events.created_at / notes.posted_at). */
  lastTouched: string | null;
}

// ---------------------------------------------------------------------
// The snapshot — THE contract.
// ---------------------------------------------------------------------
export interface ClientRecordSnapshot {
  /** clients.id — the record's canonical id (the route :clientId). */
  clientId: string;
  vitals: VitalsBand;

  // Overview dossier + Care Record both read from these projections.
  medications: MedicationRow[];
  conditions: ConditionRow[];
  appointments: AppointmentRow[];
  riskFlags: RiskFlagRow[];
  providers: ProviderRow[];
  contacts: ContactRow[];
  hospitalEvents: HospitalEventRow[];
  legalInstruments: LegalInstrumentRow[];
  coverage: CoverageRow[];
  functionalStatus: FunctionalStatusRow[];
  /** The eleventh projection (0136 — WS-3): empty = NOT RECORDED. */
  allergies: AllergyRow[];

  /** The explicit "no known allergies" confirmation date (0137 §5 —
   *  NKDA, founder ruling 2026-07-10). NULL = the question is unanswered
   *  ("No allergies recorded"); mutually exclusive with a non-empty
   *  active allergies list (server-enforced by construction). */
  allergiesNoneKnownOn: string | null;

  /** client_addresses (0137, WS-4 D2) — home + "currently at". Operator-
   *  entered rows (no note-pipeline provenance — NOT RowEnvelope). */
  addresses: AddressRow[];
  /** In-home support (0229): the answer and the provider. */
  inHomeSupport: InHomeSupportWire;

  // NOTE: documents/files are deliberately OUT of this snapshot. They are not
  // a note-pipeline projection; the Documents surface (PR-D) is sourced
  // separately. Don't amend this contract or invent a `documents` field here.
}
